import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import { useReferenceData } from "@/lib/master-data";

interface FieldDefinition {
  id: string;
  entityType: string;
  categoryCode: string | null;
  code: string;
  name: string;
  dataType: string;
  required: boolean;
  options: string[];
  displayOrder: number;
  isActive: boolean;
  version: number;
}

const TYPE_LABELS: Record<string, string> = {
  text: "文本",
  number: "数字",
  date: "日期",
  boolean: "布尔",
  select: "选项",
};

export function SettingsFieldsPage() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    dataType: "text",
    required: false,
    options: "",
    categoryCode: "",
  });
  const reference = useReferenceData();
  const query = useQuery({
    queryKey: ["field-definitions"],
    queryFn: () => apiFetch<FieldDefinition[]>("/api/v1/admin/field-definitions"),
  });

  async function create() {
    try {
      await apiFetch("/api/v1/admin/field-definitions", {
        method: "POST",
        body: JSON.stringify({
          entityType: "asset",
          categoryCode: form.categoryCode || null,
          code: form.code,
          name: form.name,
          dataType: form.dataType,
          required: form.required,
          options: form.options
            .split(/[,\n]/)
            .map((value) => value.trim())
            .filter(Boolean),
          displayOrder: (query.data?.length ?? 0) + 1,
          isActive: true,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ["field-definitions"] });
      toast.success("字段已创建");
      setOpen(false);
      setForm({
        code: "",
        name: "",
        dataType: "text",
        required: false,
        options: "",
        categoryCode: "",
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "创建失败");
    }
  }

  async function toggle(row: FieldDefinition) {
    try {
      await apiFetch(`/api/v1/admin/field-definitions/${row.id}`, {
        method: "PATCH",
        body: JSON.stringify({ id: row.id, isActive: !row.isActive }),
      });
      await queryClient.invalidateQueries({ queryKey: ["field-definitions"] });
      toast.success(row.isActive ? "字段已停用" : "字段已启用");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "更新失败");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="自定义字段"
        description="为资产添加可配置字段；启用后会在资产详情中渲染并校验。"
        actions={
          <>
            <Button variant="outline" onClick={() => query.refetch()}>
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" />
              新建字段
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>字段定义</CardTitle>
          <CardDescription>当前仅支持资产实体；字段值存于 field_values。</CardDescription>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>编码</TableHead>
                  <TableHead>名称</TableHead>
                  <TableHead>适用类别</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>必填</TableHead>
                  <TableHead>选项</TableHead>
                  <TableHead>版本</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(query.data ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs">{row.code}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      {row.categoryCode
                        ? (reference.data?.materialCategories.find(
                            (item) => item.code === row.categoryCode,
                          )?.name ?? row.categoryCode)
                        : "全部资产"}
                    </TableCell>
                    <TableCell>{TYPE_LABELS[row.dataType] ?? row.dataType}</TableCell>
                    <TableCell>{row.required ? "是" : "否"}</TableCell>
                    <TableCell className="max-w-48 truncate text-xs">
                      {row.options.join(" / ") || "—"}
                    </TableCell>
                    <TableCell>v{row.version}</TableCell>
                    <TableCell>
                      <Badge variant={row.isActive ? "success" : "secondary"}>
                        {row.isActive ? "启用" : "停用"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => toggle(row)}>
                        {row.isActive ? "停用" : "启用"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新建资产自定义字段</DialogTitle>
            <DialogDescription>编码创建后不可修改；停用后不再出现在资产详情。</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>编码</Label>
              <Input
                value={form.code}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
                placeholder="asset_owner_note"
              />
            </div>
            <div className="space-y-2">
              <Label>名称</Label>
              <Input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>类型</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.dataType}
                onChange={(event) => setForm({ ...form, dataType: event.target.value })}
              >
                <option value="text">文本</option>
                <option value="number">数字</option>
                <option value="date">日期</option>
                <option value="boolean">布尔</option>
                <option value="select">选项</option>
              </select>
            </div>
            {form.dataType === "select" ? (
              <div className="space-y-2">
                <Label>选项（逗号/换行分隔）</Label>
                <Input
                  value={form.options}
                  onChange={(event) => setForm({ ...form, options: event.target.value })}
                />
              </div>
            ) : null}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(event) => setForm({ ...form, required: event.target.checked })}
              />
              必填
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button onClick={create}>创建</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
